import React, { useState, useEffect, useMemo } from "react";
import Slider from "react-slick";
import NewsAndEventsUser from "./NewsAndEventsUser";
import ImageSlider from "./ImageSlider"; // Used for Placements
import "../../styles/homepage.css"; // Import the CSS file for styles
import { motion } from "framer-motion";
import PlacementGallery from "../user/PlacementGallery";
import { useNavigate } from "react-router-dom";
import ViewPlacementImage from "../user/ViewPlacementImage";

const useCountUp = (start, end, duration = 5000) => {
  const [count, setCount] = useState(start);

  useEffect(() => {
    let startTimestamp = null;
    const step = (timestamp) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      setCount(Math.floor(progress * (end - start) + start));

      if (progress < 1) {
        requestAnimationFrame(step);
      }
    };

    const animationFrame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(animationFrame);
  }, [start, end, duration]);

  return count;
};

const HomePage = () => {
  const [activeSection, setActiveSection] = useState("placements");
  const [currentImage, setCurrentImage] = useState(0);
  const [placementImages, setPlacementImages] = useState([]);
  const [placementImages1, setPlacementImages1] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const navigate = useNavigate();

  // Individual count-up values
  const placementCount = useCountUp(0, 100);
  const publicationsCount = useCountUp(0, 300);
  const booksCount = useCountUp(0, 25);
  const patentsCount = useCountUp(0, 14);
  const projectsCount = useCountUp(0, 30);
  const [currentInfoIndex, setCurrentInfoIndex] = useState(0);

  // Check for mobile view
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Stats data
  const stats = useMemo(() => [
    { number: placementCount, label: "Placements" },
    { number: publicationsCount, label: "Research Publications" },
    { number: booksCount, label: "Books in diverse area" },
    { number: patentsCount, label: "Patents Filed" },
    { number: projectsCount, label: "Govt. Funded Projects" }
  ], [placementCount, publicationsCount, booksCount, patentsCount, projectsCount]);

  // Recruiters data
  const recruiters = useMemo(() => [
    "Robosoft",
    "Novigo",
    "TechJini",
    "Trigent",
    "Unisys"
  ], []);

  // Institutions data
  const institutions = useMemo(() => [
    { name: "Gulf Asian English School", logo: "/images/GAES.jpg" },
    { name: "India International School", logo: "/images/IIS.jpg" },
    { name: "Creative British School", logo: "/images/CBS.jpg" },
    { name: "PACE International School", logo: "/images/PACE.jpg" },
    { name: "India International School, Mangaf", logo: "/images/IIS_KUWAIT.jpg" },
    { name: "P.A. College of Engineering", logo: "/images/PA_COLLEGE.jpg" },
    { name: "P.A. Polytechnic", logo: "/images/PA_Polytechnic.jpg" },
    { name: "P.A. College of Pharmacy", logo: "/images/PA_College-of-Pharmacy.jpg" },
    { name: "P.A. Institute of Physiotherapy", logo: "/images/PA_College-of-physiotherapy.jpg" },
    { name: "P.A. First Grade College", logo: "/images/PA_First-Grade-College.jpg" },
    { name: "PACE Residential School for Girls", logo: "/images/PRSG.jpg" },
    { name: "RIMS International School", logo: "/images/RIMS.jpg" },
    { name: "Blossom Public School", logo: "/images/Blossom.jpg" },
    { name: "University of West London", logo: "/images/UWL.jpg" },
    { name: "Delhi Private School Ajman", logo: "/images/dps.jpg" },
    { name: "PACE British School", logo: "/images/pbs.jpg" },
    { name: "PACE Modern British School", logo: "/images/pacembs.jpg" }
  ], []);

  // Improved carousel settings with responsive configurations
  const carouselSettings = {
    autoplay: true,
    autoplaySpeed: 2000,
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: isMobile ? 1 : 3,
    slidesToScroll: 1,
    centerMode: isMobile,
    centerPadding: isMobile ? "20px" : "0px",
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          centerMode: true,
          centerPadding: "0",
          focusOnSelect: true,
          adaptiveHeight: true,
          arrows: false
        }
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          centerMode: true,
          centerPadding: "10px"
        }
      }
    ]
  };

  const infoSlides = [
    "State-of-the-art infrastructure and excellent faculty.",
    "Industry-oriented curriculum with hands-on training.",
    "Outstanding placement opportunities across top companies.",
  ];

  // Fetch Placement Images
  useEffect(() => {
    const fetchPlacementImages = async () => {
      try {
        const urls = await ViewPlacementImage();
        if (urls && urls.length > 0) {
          setPlacementImages([urls[0]]);
        }
      } catch (error) {
        console.error("Error fetching placement image:", error);
      }
    };

    fetchPlacementImages();
  }, []);

  // Cycle through images
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % 2);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Section toggle effect
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveSection((prev) => (prev === "placements" ? "newsAndEvents" : "placements"));
    }, 5000);
  
    return () => clearInterval(timer);
  }, []);
  
  // Info slide cycling
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentInfoIndex((prevIndex) => (prevIndex + 1) % infoSlides.length);
    }, 3000);
  
    return () => clearInterval(interval);
  }, [infoSlides.length]);
  
  return (
    <div className="bgimage1">
      <div>
        {/* Full-Screen College Image with Apply Now & Explore Programs */}
        <ViewPlacementImage />

        {/* Placement @ PACE and News Section */}
        <div className="placements-news-container">
          {/* Placement @ PACE Slider */}
          <div className="placements-section">
            <div className="placement-slider">
              <ImageSlider /> {/* This should render individual placement cards */}
              <div className="slider-dots">
                {/* Ensure the slider dots are functional and styled */}
              </div>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="stats-section">
          <div className="stats-container">
            {stats.map((stat, index) => (
              <div key={index} className="stat-item">
                <div className="stat-number">{stat.number}+</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Research Section */}
        <div className="research-section">
          <div className="research-container">
            <div className="left-side">
              <h2 className="research-title1">Research Excellence</h2>
              <div className="research-stats">
                {stats.slice(1).map((stat, index) => (
                  <div key={index} className="research-item">
                    <div className="research-number">{stat.number}</div>
                    <div className="research-label">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="right-side">
              <img src="/images/civilhome.jpg" alt="Research at PACE" className="research-image" />
            </div>
          </div>
        </div>

        {/* YouTube and Alumni Section */}
        <div className="youtube-alumni-section">
          <iframe
            className="youtube-video"
            src="https://www.youtube.com/embed/nXT8whgZhfI?si=-fXso60sxfUx1Om4"
            title="YouTube video"
            allowFullScreen
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
          ></iframe>
          <div className="alumni-box">
            <h3 className="alumni-title">PACE Alumni</h3>
            <p className="alumni-number">12000+</p>
            <p className="alumni-description">Alumni in Leading MNCs Worldwide</p>
          </div>
        </div>

        {/* Top Recruiters Section */}
        <div className="recruiters-section">
          <Slider {...carouselSettings}>
            {recruiters.map((company) => (
              <div key={company} className="recruiter-item">
                <img src={`/images/${company.toLowerCase()}.jpg`} alt={company} className="recruiter-image" />
              </div>
            ))}
          </Slider>
        </div>

        {/* Institutions Section */}
        <div className="institutions-section">
          <div className="institutions-container">
            <h2 className="institutions-title">OUR INSTITUTIONS</h2>
            <div className="institutions-grid">
              {institutions.map((institution, index) => (
                <div key={index} className="institution-item">
                  <img src={institution.logo} alt={institution.name} className="institution-logo" />
                  <div className="institution-name">{institution.name}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
